import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import React, { useEffect, useState } from "react";
import { convertToMoney, numberFormat } from "../utils/number";
import { defaultData, labels, options } from "./config/charts/DashboardOptions";
import { formatDate, formatMonthYear } from "../utils/moment";
import {
  getEvolutivo,
  getPlanComplementarioInformacion,
  getResumenCuenta,
  getUltimoAporte,
} from "../services/dashboard";

import Constants from "./../utils/constants";
import { Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import WithMainLayout from "../hocs/layouts/MainLayout";
import { useAPIs } from "../apis/APIsConfig";
import { useDocTitle } from "../utils/hooks";
import { useGlobalStore } from "../utils/global";
import { getObtenerValorPorCodigo } from "../services/parameters";
import { sumPlanComplementarioMovimientos } from "../utils/planComplementario";
import { useFeatureFlag } from "../utils/featureFlags";

/** Numeric balance for breakdown card (same semantic as headline `resumen.monto`). */
function parseResumenMonto(monto: unknown): number {
  if (monto == null || monto === "") {
    return 0;
  }
  if (typeof monto === "number") {
    return Number.isFinite(monto) ? monto : 0;
  }
  const n = parseFloat(String(monto).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Dashboard() {
  useDocTitle("Mi Cuenta");
  useAPIs();

  const { user } = useGlobalStore();
  const mostrarComplementario = useFeatureFlag("MOSTRAR_COMPLEMENTARIO");

  const [resumen, setResumen] = useState<any>({});
  const [resumenMeses, setRentabilidadMeses] = useState<any>({});
  const [resumenParametro, setParametros] = useState<any>({});
  const [ultimoAporte, setUltimoAporte] = useState<any>({});
  const [chartData, setChartData] = useState<any>({
    labels,
    datasets: [],
  });
  const [complementarioMovSum, setComplementarioMovSum] = useState<number | null>(null);

  useEffect(() => {
    const cedula = user?.cedula;
    const token = user?.token;
    if (mostrarComplementario && cedula && token) {
      getPlanComplementarioInformacion(cedula)
        .then((resp) => {
          if (resp?.data?.successed && resp.data.value?.cuenta) {
            setComplementarioMovSum(
              sumPlanComplementarioMovimientos(resp.data.value.cuenta)
            );
          } else {
            setComplementarioMovSum(null);
          }
        })
        .catch(() => setComplementarioMovSum(null));
    } else {
      setComplementarioMovSum(null);
    }

    getResumenCuenta()
      .then((resumen) => {
        if (resumen) {
          setResumen(resumen.data.value);

          getObtenerValorPorCodigo(Constants.MESES_ESTCUENTA_OFV)
            .then((resumenParametro) => {
              if (
                resumenParametro &&
                resumenParametro.data.successed &&
                resumenParametro.data.value.estado == true
              ) {
                setParametros(resumenParametro.data.value);

                /* getRentabilidadPorMeses(resumenParametro.data.value.valor).then((resumenMeses) => {
              if (resumenMeses) {
                setRentabilidadMeses(resumenMeses.data.value);
              }
            }); */
              }
            })
            .catch(() => {});

          getUltimoAporte()
            .then((ultimoAporte) => {
              setUltimoAporte(ultimoAporte.data.value);
            })
            .catch(() => {});

          getEvolutivo()
            .then((evolutivo) => {
              const data = { ...defaultData };
              const graphData = evolutivo.data.value.reverse();
              data.labels = graphData.map((m) => {
                return formatMonthYear(m.fechaCorte);
              });
              data.datasets[0].data = graphData.map((m) => m.monSaldoFin);
              setChartData(data);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [user?.cedula, user?.token, mostrarComplementario]);

  const fondoNum = parseResumenMonto(resumen?.monto);
  const ahorrosNum = complementarioMovSum ?? 0;
  const totalCombinado = fondoNum + ahorrosNum;
  // Solo mostramos el desglose si el flag está activo Y el endpoint trajo datos de
  // complementario; si no hay datos, se oculta como si el flag estuviera apagado.
  const mostrarComplementarioCard =
    mostrarComplementario && complementarioMovSum !== null;

  return (
    <>
      <div className="block pt-14 pb-[100px] lg:pb-[140px] xl:pb-[180px]">
        <div
          className={`block w-full pb-4 md:pb-6 lg:flex lg:justify-between lg:gap-8 ${
            mostrarComplementarioCard ? "lg:items-start" : "lg:items-center"
          }`}
        >
          <div
            style={{ flex: "1 1 auto" }}
            className="block min-w-0 pb-4 lg:pb-0 lg:pr-4"
          >
            <p className="text-gray-70 text-body-1 font-body font-normal mb-2">
              Balance acumulado al corte ({resumen != undefined ? formatDate(resumen.fechaCorte, "L") : ""} )
            </p>
            <h2 className="font-bold text-primary-100">
              RD$ {resumen != undefined ? convertToMoney(resumen.monto) : 0}
            </h2>
            {mostrarComplementarioCard && (
              <div
                className="mt-3 w-full max-w-full rounded-lg border border-gray-10 px-4 py-6 md:mt-4"
                style={{ backgroundColor: "#EBE9E4" }}
                aria-label="Desglose fondo de pensiones y ahorros complementarios"
              >
                <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3 md:gap-4 md:items-end">
                  <div>
                    <p className="mb-1 text-body-2 font-body font-normal text-gray-70">Fondo de pensiones</p>
                    <p className="text-lead-lg font-bold text-neutral-black">
                      RD$ {convertToMoney(fondoNum)}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-body-2 font-body font-normal text-gray-70">Ahorros complementarios</p>
                    <p className="text-lead-lg font-bold text-neutral-black">
                      RD$ {convertToMoney(ahorrosNum)}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-body-2 font-body font-normal text-gray-70">Total</p>
                    <p className="text-lead-lg font-bold text-primary-100">
                      =RD$ {convertToMoney(totalCombinado)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <Link
            to={Constants.APPURLS.ACCOUNT_STATEMENTS}
            className="btn btn-primary btn-medium hidden lg:inline-flex"
            style={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            Estados de Cuenta
          </Link>
        </div>
        <div className="block w-full bg-neutral-white border border-gray-10 rounded-lg px-3 md:px-8 pt-3 md:pt-8 pb-3 md:pb-[22px] shadow-base mb-6 md:mb-8">
          <Line options={options} data={chartData} width={200} height={295} />
        </div>
        <Link
          to={Constants.APPURLS.ACCOUNT_STATEMENTS}
          className="btn btn-primary btn-small w-full flex lg:hidden mb-8 lg:mb-0"
        >
          Estados de Cuenta
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 shadow-box rounded-lg border border-gray-10 bg-neutral-white p-6">
          <div className="info-card">
            <p>Último Aporte ({ultimoAporte ? formatDate(ultimoAporte.fecha, "MMMM YYYY") : ""})</p>
            <h5>RD$ {ultimoAporte ? convertToMoney(ultimoAporte.valor) : 0}</h5>
          </div>
          <div className="info-card">
            <p>Rendimiento Acumulado</p>
            <h5>RD$ {resumen ? convertToMoney(resumen.montoRendimiento) : 0}</h5>
          </div>
          <div className="info-card">
            <p>Rentabilidad de los últimos {resumenParametro ? numberFormat(resumenParametro.valor) : 0} meses</p>
            <h5>{resumen ? convertToMoney(resumen.rentabilidad) : 0}%</h5>
          </div>
          <div className="info-card">
            <p>Cantidad de Cotizaciones</p>
            <h5>{resumen ? numberFormat(resumen.cantidadCotizacion) : 0}</h5>
          </div>
        </div>
      </div>
    </>
  );
}

export default WithMainLayout(Dashboard, "Mi Cuenta");
